import { useMemo, useState, type FormEvent } from 'react';
import { ApiError, api, setRefreshToken, setToken } from '../lib/api';
import { PasswordInput } from '../components/PasswordInput';
import { PasswordMatchValidator } from '../components/PasswordMatchValidator';

/**
 * Pantalla pública /auth/setup-account?token=XXX (v1.3.2, flujo Opción A).
 * El usuario invitado define su contraseña → auto-login. Montada fuera de
 * AuthProvider (ver App.tsx / PublicAuthRouter), patrón /legal.
 */
export function SetupAccountScreen() {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get('token') ?? '',
    [],
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const policyError = (pwd: string): string | null => {
    if (pwd.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
    if (!/[A-Z]/.test(pwd)) return 'Incluye al menos una letra mayúscula.';
    if (!/[0-9]/.test(pwd)) return 'Incluye al menos un número.';
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    const pe = policyError(newPassword);
    if (pe) { setError(pe); return; }

    setSubmitting(true);
    try {
      const res = await api<{ accessToken: string; refreshToken: string }>(
        '/auth/setup-account',
        { method: 'POST', body: { token, newPassword } },
      );
      // Auto-login: guardamos el par y recargamos a la app.
      setToken(res.accessToken);
      setRefreshToken(res.refreshToken);
      window.location.href = '/';
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          'Este enlace expiró o ya fue usado. Pide a tu administrador que te invite de nuevo.',
        );
      } else {
        setError('No se pudo activar la cuenta. Intenta de nuevo.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-sm rounded-2xl border border-slate-200/80 bg-white p-8">
        <div className="mb-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
            FluidSol
          </p>
          <h1 className="font-serif text-2xl tracking-tight text-slate-900">
            Activa tu cuenta
          </h1>
        </div>

        {!token ? (
          <div className="space-y-4">
            <p className="alert-over rounded-xl px-3 py-2 text-xs">
              Enlace inválido: falta el token de invitación.
            </p>
            <a
              href="/"
              className="block w-full rounded-xl bg-slate-900 px-4 py-2.5 text-center text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Ir al inicio de sesión
            </a>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-sm text-slate-500">
              Crea tu contraseña para acceder a tu cuenta.
            </p>
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Nueva contraseña
              </span>
              <PasswordInput
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Confirmar contraseña
              </span>
              <PasswordInput
                value={confirm}
                onChange={setConfirm}
                autoComplete="new-password"
                required
              />
              <PasswordMatchValidator password={newPassword} confirmPassword={confirm} />
            </label>
            <p className="text-[11px] text-slate-400">
              Mínimo 8 caracteres, una mayúscula y un número.
            </p>
            {error && (
              <p className="alert-over rounded-xl px-3 py-2 text-xs">{error}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? 'Activando…' : 'Activar cuenta'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default SetupAccountScreen;
